import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Eye } from 'lucide-react';
import { formatDate } from '@/utils/formatting';
import { cn } from '@/lib/utils';
import type { Version } from '../types';
import { useDeleteVersion } from '@/hooks/useVersion';
import { supabase } from '@/lib/supabase';
import { showToast } from '@/utils/toast';

interface VersionTableProps {
  versions: Version[];
  onDelete: (id: number, force: boolean) => Promise<void>;
  isDeleting: boolean;
}

export function VersionTable({ versions, onDelete, isDeleting }: VersionTableProps) {
  const navigate = useNavigate();
  const [regionNames, setRegionNames] = useState<Record<number, string>>({});

  // Fetch region names for all versions
  useEffect(() => {
    if (versions.length > 0) {
      const fetchRegionNames = async () => {
        // Get unique region IDs
        const regionIds = [...new Set(versions
          .filter(v => v.region_id)
          .map(v => v.region_id))];
          
        if (regionIds.length === 0) return;
        
        // Fetch region data
        const { data, error } = await supabase
          .from('regions')
          .select('id, name')
          .in('id', regionIds.filter((id): id is number => id !== undefined));
          
        if (!error && data) {
          // Create a map of region ID to name
          const nameMap: Record<number, string> = {};
          data.forEach(region => {
            nameMap[region.id] = region.name;
          });
          setRegionNames(nameMap);
        }
      };
      
      fetchRegionNames();
    }
  }, [versions]);

  const columns = useMemo(() => [
    {
      header: 'Version',
      cell: (version: Version) => version.version_number
    },
    {
      header: 'Status',
      cell: (version: Version) => (
        <Badge 
          variant={version.status === 'published' ? 'default' : 'secondary'}
          className={cn(
            version.status === 'published' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
            'capitalize'
          )}
        >
          {version.status}
        </Badge>
      )
    },
    {
      header: 'Region',
      cell: (version: Version) => {
        return version.region_id ? (
          <Badge variant="outline" className="bg-slate-50 dark:bg-slate-900">
            {regionNames[version.region_id] || `Region ${version.region_id}`}
          </Badge>
        ) : '-';
      }
    },
    {
      header: 'Created',
      cell: (version: Version) => formatDate(version.created_at)
    },
    {
      header: 'Effective Date',
      cell: (version: Version) => {
        if (version.status === 'published' && version.effective_date) {
          return formatDate(version.effective_date);
        }
        return '-';
      }
    },
    {
      header: 'Published',
      cell: (version: Version) => {
        if (version.status === 'published' && version.published_at) {
          return formatDate(version.published_at);
        }
        return '-';
      }
    },
    {
      header: 'Actions',
      cell: (version: Version) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Check if version has a region_id
              if (version.region_id) {
                navigate(`/providers/${version.provider_id}/regions/${version.region_id}/versions/${version.id}/dashboard`);
              } else {
                navigate(`/providers/${version.provider_id}/versions/${version.id}/dashboard`);
              }
            }}
          >
            <Eye className="h-4 w-4 mr-2" />
            View
          </Button>
          <VersionActions version={version} providerId={version.provider_id} />
        </div>
      )
    }
  ], [navigate, onDelete, isDeleting, regionNames]);

  if (!versions?.length) {
    return (
      <div className="text-center py-8 border rounded-md bg-muted/10">
        <p className="text-muted-foreground">No versions found</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column, index) => (
              <TableHead key={index}>{column.header}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map((version) => (
            <TableRow key={version.id}>
              {columns.map((column, index) => (
                <TableCell key={index}>
                  {column.cell(version)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface Props {
  version: Version;
  providerId: number;
}

export function VersionActions({ version, providerId }: Props) {
  const navigate = useNavigate();
  const deleteVersion = useDeleteVersion(providerId);

  const handleDelete = async () => {
    const message = version.status === 'published'
      ? 'Are you sure you want to delete this published version? This action cannot be undone.'
      : 'Are you sure you want to delete this version? This action cannot be undone.';

    if (window.confirm(message)) {
      try {
        // Pass force=true for published versions
        await deleteVersion.mutateAsync({
          versionId: version.id,
          force: version.status === 'published'
        });
        
        showToast.deleted(`Version ${version.version_number}`);
      } catch (error) {
        showToast.deleteFailed(`version ${version.version_number}`);
        console.error('Delete version error:', error);
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => {
          // Check if version has a region_id
          if (version.region_id) {
            navigate(`regions/${version.region_id}/versions/${version.id}`);
          } else {
            navigate(`versions/${version.id}`);
          }
        }}>
          View Details
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={handleDelete}
          className="text-red-600 focus:text-red-600"
        >
          Delete Version
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}